import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, FileCog, ClipboardList, Settings, Users, Archive, LogOut, ShieldCheck, Globe, KeyRound, ScrollText } from 'lucide-react';

const ICON_SIZE = 15;
const ICON_STROKE = 1.75;

const NavIcon = ({ icon: Icon }) => (
  <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} className="shrink-0" />
);

const Navbar = () => {
  const { user, roles, logout, keycloak, canProduce, canSettings, isAdmin } = useAuth();
  const location = useLocation();

  const isActive = (path) =>
    path !== '#' && (location.pathname === path || (path !== '/' && location.pathname.startsWith(path)));

  const keycloakAdminUrl = keycloak?.authServerUrl
    ? `${keycloak.authServerUrl.replace(/\/+$/, '')}/admin/${keycloak.realm || 'bank-support'}/console/`
    : null;

  const navItems = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/reporting/produce', label: 'Produire reporting', icon: FileCog, show: canProduce },
    { path: '/reporting/consult', label: 'Consulter reporting', icon: ClipboardList },
    { path: '/settings', label: 'Paramétrages', icon: Settings, show: canSettings },
    { path: keycloakAdminUrl || '#', label: 'Gestion des utilisateurs', icon: Users, external: !!keycloakAdminUrl, disabled: !keycloakAdminUrl, show: isAdmin },
    { path: '/archives', label: 'Archivages', icon: Archive },
    { path: '/reporting/audit', label: "Journal d'audit", icon: ScrollText },
    { path: '/license', label: 'Licence', icon: KeyRound, show: isAdmin },
    { path: '#', label: 'Conformité COBAC', icon: ShieldCheck, disabled: true },
    { path: '#', label: 'Reporting BEAC', icon: Globe, disabled: true },
  ].filter(item => item.show !== false);

  const ROLE_LABELS = {
    reporting_access: 'Reporting',
    reporting_produce: 'Reporting',
    reporting_cid: 'Reporting',
    reporting_settings: 'Reporting',
    admin_apps: 'Admin',
    support: 'Support',
    auditeur: 'Auditeur',
    manager: 'Manager',
    technicien: 'Technicien',
  };

  const filteredRoles = (() => {
    const skip = r => r.startsWith('default-') || r.startsWith('offline_') || r.startsWith('uma_');
    const labeled = roles
      .filter(r => !skip(r))
      .map(r => ROLE_LABELS[r] || r);
    return [...new Set(labeled)].slice(0, 2);
  })();

  return (
    <nav className="bg-white border-b border-slate-200 shadow-sm">
      <div className="w-full px-4">
        <div className="flex items-center justify-between h-14">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 text-[#004A99] hover:text-[#003d80] shrink-0 mr-2">
            <img src="/logo1.jpeg" alt="Logo" className="w-8 h-8 object-contain" />
            <span className="text-sm font-bold tracking-tight leading-tight">
              Support<br />Bancaire
            </span>
          </Link>

          {/* Nav items */}
          <div className="hidden md:flex items-stretch flex-1 overflow-hidden">
            {navItems.map(({ path, label, icon, disabled, external }) => {
              if (disabled) {
                return (
                  <span
                    key={label}
                    title="Bientôt disponible"
                    className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-slate-300 cursor-not-allowed select-none whitespace-nowrap"
                  >
                    <NavIcon icon={icon} />
                    <span>{label}</span>
                    <span className="text-[9px] bg-slate-100 text-slate-400 px-1 py-px rounded leading-none">bientôt</span>
                  </span>
                );
              }

              if (external) {
                return (
                  <a
                    key={path}
                    href={path}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1.5 px-3 h-full text-xs font-medium whitespace-nowrap border-b-2 border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-200 transition-colors"
                  >
                    <NavIcon icon={icon} />
                    <span>{label}</span>
                  </a>
                );
              }

              const active = isActive(path);
              return (
                <Link
                  key={path}
                  to={path}
                  className={`flex items-center gap-1.5 px-3 h-full text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                    active
                      ? 'text-[#004A99] border-[#E20613]'
                      : 'text-slate-600 hover:text-slate-900 border-transparent hover:border-slate-200'
                  }`}
                >
                  <NavIcon icon={icon} />
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>

          {/* User + Logout */}
          <div className="flex items-center gap-3 shrink-0 ml-2">
            <div className="hidden lg:flex flex-col items-end leading-tight">
              <span className="text-xs font-semibold text-slate-800">
                {user?.fullName || user?.preferred_username || user?.username}
              </span>
              {filteredRoles.length > 0 && (
                <span className="text-[10px] text-slate-400">{filteredRoles.join(', ')}</span>
              )}
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 text-xs font-medium rounded transition-colors"
            >
              <LogOut size={ICON_SIZE} strokeWidth={ICON_STROKE} className="shrink-0" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>

        </div>
      </div>
    </nav>
  );
};

export default Navbar;
